import React, { useState, useEffect } from "react";
import DataGrid, {
  Column,
  Grouping,
  GroupPanel,
  Item,
  Paging,
  SearchPanel,
  Toolbar,
} from "devextreme-react/data-grid";
import { getData } from "../../helpers";
import { Button, Spinner } from "react-bootstrap";
import DatePicker from "react-datepicker";

const DynamicTableOccupation = ({ IdCluster }) => {
  const [ocupationData, setOcupationData] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const getOcupationData = async () => {
    const current = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    const end = new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0);

    const data = {
      IdCluster,
      start_date: current,
      end_date: end,
    };

    try {
      setLoading(true);

      const response = await getData(
        `delivery-dashboard-dynamic-ocupation?Cluster=${data.IdCluster}&StartDate=${data.start_date}&EndDate=${data.end_date}`,
        []
      );

      setOcupationData(response);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const buildColumns = (data) => {
    if (!data || data.length === 0) return [];

    const baseColumns = [
      {
        caption: "Proyecto",
        selector: "Proyecto",
        width: 290,
      },
      {
        caption: "Recurso",
        selector: "NombreUsuario",
        width: 160,
        customRender: (row) => <strong>{row.data.NombreUsuario}</strong>,
      },
    ];

    const dynamicKeys = Object.keys(data[0]).filter(
      (key) => key !== "NombreUsuario" && key !== "Proyecto"
    );

    // función para asignar colores al porcentaje
    const getColor = (percentage) => {
      if (percentage >= 0 && percentage < 31) return "#008700";
      if (percentage >= 31 && percentage < 61) return "#e8e812ff";
      if (percentage >= 61 && percentage < 91) return "#FFA400";
      if (percentage >= 91) return "#FC0200";
      return "#7f8c8d";
    };

    const dynamicColumns = dynamicKeys.map((key) => ({
      caption: key,
      selector: key,
      width: 300,
      customRender: (row) => {
        const valores = row.data[key]?.split(",").map((v) => v.trim()) || [];

        return (
          <>
            {valores.map((valor, idx) => {
              if (valor.toLowerCase().includes("horas planeadas")) {
                return (
                  <p key={idx}>
                    <strong>Horas Planeadas:</strong>{" "}
                    <span style={{ color: "#29B9D0" }}>
                      {valor.replace("Horas Planeadas:", "").trim()}
                    </span>
                  </p>
                );
              }

              if (valor.toLowerCase().includes("horas reales")) {
                return (
                  <p key={idx}>
                    <strong>Horas Reales:</strong>{" "}
                    <span style={{ color: "#6AB41B" }}>
                      {valor.replace("Horas Reales:", "").trim()}
                    </span>
                  </p>
                );
              }

              if (valor.toLowerCase().includes("ocupación")) {
                const match = valor.match(/([\d.]+)\s*%/);
                if (match) {
                  const num = parseFloat(match[1]);
                  const color = getColor(num);
                  const before = valor.split(match[0])[0];
                  return (
                    <p key={idx}>
                      <strong>{before}</strong>
                      <span style={{ color }}>{num}%</span>
                    </p>
                  );
                }
              }

              return <p key={idx}>{valor}</p>;
            })}
          </>
        );
      },
    }));

    return [...baseColumns, ...dynamicColumns];
  };

  const columns = buildColumns(ocupationData);

  const tagsHeader = (data) => {
    return data?.map((activity) => ({ ...activity }));
  };

  useEffect(() => {
    setOcupationData([]);
  }, [startDate, endDate]);

  return (
    <div
      style={{
        padding: "20px",
        backgroundColor: "#f8f9fa",
        minHeight: "100vh",
      }}
    >
      <div style={{ marginBottom: "30px", textAlign: "center" }}>
        <h1
          style={{
            margin: "0 0 10px 0",
            color: "#E0301E",
            fontSize: "28px",
            fontWeight: "600",
          }}
        >
          Tablas dinámicas de ocupación
        </h1>
        <p style={{ color: "#7f8c8d", margin: 0, fontSize: "16px" }}>
          Distribución de horas planeadas y reales por recurso y proyecto, con
          columnas que se adaptan dinámicamente según los meses seleccionados
        </p>
      </div>

      <div
        style={{
          display: "flex",
          gap: "20px",
          alignItems: "flex-end",
          marginBottom: "30px",
          padding: "20px",
          backgroundColor: "#f8f9fa",
          borderRadius: "6px",
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <label
            style={{ fontWeight: "500", color: "#495057", fontSize: "14px" }}
          >
            Fecha Inicio:
          </label>
          <DatePicker
            selected={startDate}
            onChange={(date) => date && setStartDate(date)}
            dateFormat="MM/yyyy"
            showMonthYearPicker
            className="form-control"
            maxDate={endDate}
          />
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <label
            style={{ fontWeight: "500", color: "#495057", fontSize: "14px" }}
          >
            Fecha Fin:
          </label>
          <DatePicker
            selected={endDate}
            onChange={(date) => date && setEndDate(date)}
            dateFormat="MM/yyyy"
            showMonthYearPicker
            className="form-control"
            minDate={startDate}
          />
        </div>

        <Button
          className="ml-2"
          variant="primary"
          onClick={getOcupationData}
          disabled={loading}
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="mr-2" />
              Cargando...
            </>
          ) : (
            <>
              <i className="fa fa-search mr-2" />
              Buscar
            </>
          )}
        </Button>
      </div>

      {ocupationData.length > 0 && (
        <div>
          <div style={{ overflowX: "auto" }}>
            <DataGrid
              dataSource={tagsHeader(ocupationData)}
              allowColumnReordering={true}
              showBorders={true}
              allowColumnResizing={true}
              columnAutoWidth={true}
              width="100%"
              rowAlternationEnabled={true}
              height="80vh"
            >
              <GroupPanel
                visible={true}
                emptyPanelText="Arrastra el encabezado de una columna aquí para agrupar por esa columna"
              />
              <SearchPanel visible={true} placeholder="Buscar..." />
              <Grouping autoExpandAll={expanded} />
              <Toolbar>
                <Item name="groupPanel" />
                <Item location="after">
                  <button
                    onClick={() => setExpanded((prev) => !prev)}
                    style={{
                      backgroundColor: "#E0311E",
                      border: "1px solid #E0311E",
                      color: "#ffffff",
                      borderRadius: "4px",
                      height: "35px",
                      padding: "0 12px",
                      fontSize: "14px",
                      cursor: "pointer",
                    }}
                  >
                    {expanded ? "Colapsar filas" : "Expandir filas"}
                  </button>
                </Item>
                <Item name="searchPanel" />
              </Toolbar>
              {columns.map((itemCol, index) => (
                <Column
                  key={index}
                  dataField={itemCol.selector}
                  caption={itemCol.caption}
                  width={itemCol.width}
                  headerCellRender={() => (
                    <div
                      style={{
                        color: "black",
                        fontWeight: "600",
                        paddingTop: "7px",
                        paddingBottom: "7px",
                        textAlign: "center",
                      }}
                    >
                      {itemCol.caption}
                    </div>
                  )}
                  cellRender={(row) =>
                    itemCol.customRender ? itemCol.customRender(row) : row.text
                  }
                />
              ))}
              <Paging defaultPageSize={8} />
            </DataGrid>
          </div>
        </div>
      )}

      {ocupationData.length === 0 && !loading && (
        <div style={{ textAlign: "center", padding: "40px", color: "#6c757d" }}>
          <div style={{ fontSize: "48px", marginBottom: "20px" }}>📊</div>
          <h4>Sin datos disponibles</h4>
          <p>
            Selecciona un rango de fechas y haz clic en "Buscar" para ver los
            datos de ocupación
          </p>
        </div>
      )}

      {loading && (
        <div style={{ textAlign: "center", padding: "40px", color: "#6c757d" }}>
          <div style={{ display: "inline-block", marginBottom: "20px" }}>
            <Spinner
              animation="border"
              variant="primary"
              style={{ width: "3rem", height: "3rem" }}
            />
          </div>
          <h4>Cargando datos...</h4>
        </div>
      )}
    </div>
  );
};

export default DynamicTableOccupation;
